import {Component, HostListener, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {MessageService} from "primeng/api";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {DefaultDecoder} from "../../../../model/default-decoder";
import {DefaultDecodersService} from "../../../../services/default-decoders.service";
import {DefaultDecoderPersistRequest} from "../../../../model/requests/default-decoder-persist-request";

@Component({
  selector: 'app-default-decoders-form',
  templateUrl: './default-decoders-form.component.html',
  styleUrls: ['./default-decoders-form.component.scss']
})
export class DefaultDecodersFormComponent implements OnInit {

  public defaultDecoder: DefaultDecoder;

  public types: string[];

  public decoderForm: FormGroup;
  public persistingChanges: any;
  public sourceVisible: boolean;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { oldName: string };

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private defaultDecodersService: DefaultDecodersService,
              private messageService: MessageService,
              private commonMessagesService: CommonMessagesService) {
    this.types = ['jar', 'js', 'process'];
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[1].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.defaultDecoder = new DefaultDecoder();
    this.createForm(this.defaultDecoder);
  }

  loadEditionForm(name): void {
    this.defaultDecodersService.getDefaultDecoder(name).toPromise().then(response => {
      this.defaultDecoder = response.defaultDecoder;
      this.editInfo = {oldName: this.defaultDecoder.name};
      this.createForm(this.defaultDecoder);
    });
  }

  private createForm(defaultDecoder: DefaultDecoder): void {
    this.decoderForm = this.formBuilder.group({
      name: [defaultDecoder.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      type: [defaultDecoder.type, [Validators.required, Validators.maxLength(255)]],
      source: [defaultDecoder.source, [Validators.maxLength(255)]],
      routingKey: [defaultDecoder.routingKey, [Validators.required, Validators.maxLength(255)]]
    });
    this.decoderForm.markAsPending();
  }

  typeChanged(event: any): void {
    if (event.value === 'js') {
      this.sourceVisible = true;
    } else {
      this.sourceVisible = false;
      this.decoderForm.get('source').setValue(null);
    }

    if (event.value === 'js') {
      this.decoderForm.get('routingKey').disable();
      this.decoderForm.get('routingKey').setValue('.sandboxjs');
    } else if (event.value === 'jar') {
      this.decoderForm.get('routingKey').disable();
      this.decoderForm.get('routingKey').setValue('.rawmessage');
    } else if (event.value === 'process') {
      this.decoderForm.get('routingKey').enable();
    }
  }

  persistDefaultDecoderData(): void {
    if (!this.decoderForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    if (!this.processIsValid(persistData)) {
      const summary = this.i18n.translate('error.summary.invalidField');
      const detail = this.i18n.translate('error.decoder.invalidRoutingKey');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
      this.persistingChanges = false;
      return;
    }

    let promise;
    if (this.mode === 'create') {
      promise = this.defaultDecodersService.createDefaultDecoder(persistData).toPromise();
    } else {
      promise = this.defaultDecodersService.editDefaultDecoder(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/hw-catalog`], {state: {showView: 'decoders'}});
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  public cancel(): void {
    this.router.navigate([`/system/hw-catalog`], {state: {showView: 'decoders'}});
  }

  private buildPersistData(): DefaultDecoderPersistRequest {
    const formValue = this.decoderForm.getRawValue();
    return {
      name: formValue.name,
      type: formValue.type,
      source: formValue.source,
      routingKey: formValue.routingKey,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null
    };
  }

  private processIsValid(persistData: DefaultDecoderPersistRequest): boolean {
    return !(persistData.type === 'process' && persistData.routingKey === '.rawmessage');
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.decoderForm.untouched;
  }

}
